import { createReducer, on } from '@ngrx/store';
import * as AssocActions from './ingredient-association.actions';
import {
  IngredientAssociation,
  IngredientAssociationAiApplyResponse,
  IngredientAssociationAiSuggestion,
} from '../../../shared/models/ingredientAssociation.model';

export interface IngredientAssociationState {
  associations: IngredientAssociation[];
  loading: boolean;
  loaded: boolean;
  error: any | null;
  aiSuggestions: IngredientAssociationAiSuggestion[];
  aiConceptCount: number;
  aiGenerationLoading: boolean;
  aiGenerationCompleted: boolean;
  aiApplyLoading: boolean;
  aiApplyResult: IngredientAssociationAiApplyResponse | null;
  // we could also keep lastFromIdFilter?: number
}

export const initialIngredientAssociationState: IngredientAssociationState = {
  associations: [],
  loading: false,
  loaded: false,
  error: null,
  aiSuggestions: [],
  aiConceptCount: 0,
  aiGenerationLoading: false,
  aiGenerationCompleted: false,
  aiApplyLoading: false,
  aiApplyResult: null
};

export const ingredientAssociationReducer = createReducer(
  initialIngredientAssociationState,

  // LOAD
  on(AssocActions.loadIngredientAssociations, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AssocActions.loadIngredientAssociationsSuccess, (state, { associations }) => ({
    ...state,
    loading: false,
    loaded: true,
    associations,
    error: null
  })),
  on(AssocActions.loadIngredientAssociationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // ADD
  on(AssocActions.addIngredientAssociationSuccess, (state, { association }) => ({
    ...state,
    associations: [...state.associations, association]
  })),
  on(AssocActions.addIngredientAssociationFailure, (state, { error }) => ({
    ...state,
    error
  })),

  // UPDATE
  on(AssocActions.updateIngredientAssociationSuccess, (state, { association }) => ({
    ...state,
    associations: state.associations.map(a =>
      a.association_id === association.association_id ? association : a
    )
  })),
  on(AssocActions.updateIngredientAssociationFailure, (state, { error }) => ({
    ...state,
    error
  })),

  // DELETE
  on(AssocActions.deleteIngredientAssociationSuccess, (state, { association_id }) => ({
    ...state,
    associations: state.associations.filter(a => a.association_id !== association_id)
  })),
  on(AssocActions.deleteIngredientAssociationFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(AssocActions.generateIngredientAssociationsWithAi, (state) => ({
    ...state,
    aiGenerationLoading: true,
    aiGenerationCompleted: false,
    aiSuggestions: [],
    aiConceptCount: 0,
    aiApplyResult: null,
    error: null
  })),
  on(AssocActions.generateIngredientAssociationsWithAiSuccess, (state, { suggestions, conceptCount }) => ({
    ...state,
    aiGenerationLoading: false,
    aiGenerationCompleted: true,
    aiSuggestions: suggestions,
    aiConceptCount: conceptCount
  })),
  on(AssocActions.generateIngredientAssociationsWithAiFailure, (state, { error }) => ({
    ...state,
    aiGenerationLoading: false,
    error
  })),

  on(AssocActions.applySelectedIngredientAssociations, (state) => ({
    ...state,
    aiApplyLoading: true,
    aiApplyResult: null,
    error: null
  })),
  on(AssocActions.applySelectedIngredientAssociationsSuccess, (state, { result }) => ({
    ...state,
    aiApplyLoading: false,
    aiApplyResult: result,
    aiSuggestions: [],
    aiConceptCount: 0,
    associations: [...state.associations, ...result.created]
  })),
  on(AssocActions.applySelectedIngredientAssociationsFailure, (state, { error }) => ({
    ...state,
    aiApplyLoading: false,
    error
  })),

  // RESET
  on(AssocActions.resetIngredientAssociations, () => ({
    ...initialIngredientAssociationState
  }))
);
